import * as Sentry from '@sentry/browser';
import { UseQueryResult } from '@tanstack/react-query';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';
import { STALE_TIMES, GC_TIMES } from '@ais-ui/custom-hooks';
import { WordpressReleaseNote } from '@ais-ui/utils';

const RELEASE_NOTES_URL =
  'https://gatehousemaritime.com/wp-json/marinepulse/v1/getreleasenotes/';

/**
 * The WordPress endpoint echoes the request's Origin into
 * `Access-Control-Allow-Origin`, but its CDN/page cache doesn't `Vary` on
 * Origin — so a response cached for one MDM host (e.g. mdmtest) is served to
 * another (e.g. mdm) and the browser blocks it as a CORS error. The endpoint
 * ignores unknown query params, so keying the URL by host gives each origin
 * its own cache entry.
 */
export const getReleaseNotesUrl = (host: string): string =>
  `${RELEASE_NOTES_URL}?origin=${encodeURIComponent(host)}`;

const fetchReleaseNotes = async () => {
  const url = getReleaseNotesUrl(window.location.host);
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    // A CORS rejection surfaces only as `TypeError: Failed to fetch` inside
    // React Query and would otherwise never reach Sentry as an issue.
    Sentry.captureException(error, {
      level: 'warning',
      fingerprint: ['release-notes-fetch-failed'],
      tags: { feature: 'release-notes' },
      extra: { url },
    });
    throw error;
  }
};

export const useReleaseNotes = (
  disabled: boolean,
): UseQueryResult<WordpressReleaseNote[], Error> => {
  const onPremise = import.meta.env.VITE_ON_PREMISE === 'true';

  const fetchArticlesConditionally = async () => {
    if (!onPremise) {
      return fetchReleaseNotes();
    } else {
      const response = await fetch('release_notes.json');
      if (!response.ok) {
        throw new Error('Failed to fetch release notes from local JSON');
      }
      return response.json();
    }
  };

  return useQuery(
    {
      queryKey: ['releaseNotes'],
      queryFn: fetchArticlesConditionally,
      enabled: !disabled,
      staleTime: STALE_TIMES.STATIC,
      gcTime: GC_TIMES.NONE,
    },
    'releaseNotes:list',
  );
};
